import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowDown } from "lucide-react";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.12 },
  }),
};

const titleLines = ["Premium Websites", "That Help", "Businesses Grow"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-32 pb-20"
    >
      {/* ambient gold glow */}
      <motion.div
        style={{ y }}
        className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#d4af37]/10 blur-[140px]"
      />
      <motion.div
        style={{ y: useTransform(scrollYProgress, [0, 1], [0, -120]) }}
        className="pointer-events-none absolute bottom-0 -left-40 w-[500px] h-[500px] rounded-full bg-[#d4af37]/5 blur-[120px]"
      />

      <motion.div style={{ opacity }} className="relative max-w-[1400px] mx-auto px-6 md:px-12 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="flex items-center gap-4 mb-10"
        >
          <span className="h-px w-12 bg-[#d4af37]" />
          <span className="text-xs uppercase tracking-[0.3em] gold-text">
            Freelance Web Designer
          </span>
        </motion.div>

        <h1 className="font-serif-display font-light leading-[0.95] text-5xl sm:text-7xl lg:text-8xl tracking-tight">
          {titleLines.map((t, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {i === 2 ? (
                  <>
                    Businesses <span className="italic gold-text">Grow</span>
                  </>
                ) : (
                  t
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 grid lg:grid-cols-[1fr_auto] gap-10 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="max-w-xl text-lg md:text-xl text-white/60 leading-relaxed"
          >
            I design modern, responsive, high-converting websites for businesses.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="flex flex-wrap items-center gap-4"
          >
            <a
              href="#portfolio"
              data-testid="hero-portfolio-btn"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#d4af37] text-black font-medium tracking-wide hover:bg-[#f3e5ab] transition-colors duration-300"
            >
              View Portfolio
              <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
            </a>
            <a
              href="#contact"
              data-testid="hero-contact-btn"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white tracking-wide hover:border-[#d4af37] hover:text-[#d4af37] transition-colors duration-300"
            >
              Contact Me
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
}
